import { getSupabase } from '@/lib/supabase';
import type { TemplateSnapshot } from '@/types/nutrition';
import { api, firstValidationError, withStatus, type ApiResponse } from '@/lib/apiClient';
import type { SaveTemplateBody } from '@schemas/nutrition';

export interface NutritionTemplateRow {
  id: string;
  coach_id: string;
  title: string;
  notes: string | null;
  tags: string[] | null;
  difficulty?: string;
  duration?: string;
  thumbnail?: string | null;
  is_public?: boolean;
  target_calories?: number | null;
  target_protein_pct?: number | null;
  target_carbs_pct?: number | null;
  target_fat_pct?: number | null;
  pdf_object_path?: string | null;
  template_data: any; // Direct editor format (DayType[])
  cycle_length?: number | null;
  is_archived?: boolean;
  created_at: string;
  updated_at: string;
}

export async function listTemplates(): Promise<NutritionTemplateRow[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb
    .from('nutrition_templates')
    .select('*') // Select all columns for now
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getTemplate(id: string): Promise<NutritionTemplateRow | null> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.from('nutrition_templates').select('*').eq('id', id).single();
  if (error?.code === 'PGRST116') return null;
  if (error) throw error;
  return data;
}

export async function createTemplate(payload: {
  title: string;
  notes?: string | null;
  tags?: string[] | null;
  snapshot: TemplateSnapshot;
}) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  console.log('🔧 Creating template with payload:', {
    title: payload.title,
    notes: payload.notes,
    tags: payload.tags,
    snapshotDaysCount: payload.snapshot?.days?.length || 0,
    userId: user.id,
  });

  const insertPayload = {
    coach_id: user.id,
    title: payload.title,
    notes: payload.notes ?? null,
    tags: payload.tags ?? [],
    template_snapshot: payload.snapshot, // ✅ writes to template_snapshot
  };

  console.log('🔧 Insert payload:', insertPayload);

  const { data, error } = await sb.from('nutrition_templates').insert(insertPayload).select().single();

  if (error) {
    console.error('❌ Database insert error:', error);
    throw error;
  }

  console.log('✅ Template created successfully:', data);
  return data as NutritionTemplateRow;
}

export async function updateTemplate(
  id: string,
  patch: {
    title?: string;
    notes?: string | null;
    tags?: string[] | null;
    snapshot?: TemplateSnapshot;
  },
) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb
    .from('nutrition_templates')
    .update({
      ...(patch.title !== undefined ? { title: patch.title } : {}),
      ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
      ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
      ...(patch.snapshot !== undefined ? { template_snapshot: patch.snapshot } : {}), // ✅ updates template_snapshot
    })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as NutritionTemplateRow;
}

export async function deleteTemplate(id: string) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error } = await sb.from('nutrition_templates').delete().eq('id', id);
  if (error) throw error;
}

export async function upsertNutritionTemplate(input: {
  id?: string;
  title: string;
  notes?: string | null;
  tags?: string[] | null;
  editorData?: any; // Direct editor format (DayType[])
  snapshot?: any; // Back-compat alias used by older callers
  targetCalories?: number;
  proteinPct?: number;
  carbsPct?: number;
  fatPct?: number;
  difficulty?: string;
  duration?: string;
  pdfObjectPath?: string | null;
}) {
  const resolvedEditorData =
    (Array.isArray(input.editorData) && input.editorData) ||
    (Array.isArray(input.snapshot) && input.snapshot) ||
    (Array.isArray(input.snapshot?.dayTypes) && input.snapshot.dayTypes) ||
    (Array.isArray(input.snapshot?.days) && input.snapshot.days) ||
    [];

  // BFF call through Workers to use httpOnly auth
  console.log('🔧 Upserting template via Worker:', {
    id: input.id,
    title: input.title,
    editorDataDaysCount: resolvedEditorData.length || 0,
    targetCalories: input.targetCalories,
    difficulty: input.difficulty,
  });

  const res = await api.post<unknown>('/api/coach/nutrition-templates/save', {
    id: input.id,
    title: input.title,
    notes: input.notes ?? null,
    tags: input.tags ?? [],
    editorData: resolvedEditorData, // Direct editor format only
    targetCalories: input.targetCalories,
    proteinPct: input.proteinPct,
    carbsPct: input.carbsPct,
    fatPct: input.fatPct,
    difficulty: input.difficulty,
    duration: input.duration,
    pdfObjectPath: input.pdfObjectPath ?? null,
  } satisfies SaveTemplateBody);

  if (!res.ok) {
    const err = res.body as { error?: string; message?: string } | undefined;
    throw new Error(
      firstValidationError(err) ||
        err?.error ||
        err?.message ||
        withStatus(res, (status) => `Failed to save template (${status})`),
    );
  }
  return (res as { item?: NutritionTemplateRow }).item as NutritionTemplateRow;
}

/** Delete a template via the Worker (unlike the direct-Supabase `deleteTemplate` above). Non-throwing. */
export function deleteTemplateViaApi(templateId: string): Promise<ApiResponse> {
  return api.delete(`/api/coach/nutrition-templates/${encodeURIComponent(templateId)}`);
}
